import type {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  Method,
  RawAxiosRequestConfig,
} from 'axios';
import axios from 'axios';
import { buildSelfUrl } from '@/connection/url-retriever';
import {
  checkToken,
  handleInvalidToken,
  resetSessionTimer,
  setToken,
} from '@/shared/api/clients/utilities/authorization';
import {
  errorHideProgressBar,
  hideProgressBar,
  showProgressBar,
} from '@/shared/api/clients/utilities/progressBar';
import type { ExtendedInternalAxiosRequestConfig } from '@/shared/api/clients/types';
import type { IHttpClient, IHttpRequestConfig } from './HttpClient';

const TIMEOUT_DEFAULT = 60_000;

export class HttpAxiosClient implements IHttpClient {
  protected axios: AxiosInstance;

  constructor() {
    const config: RawAxiosRequestConfig = {
      headers: {
        'Access-Control-Allow-Origin': buildSelfUrl(),
        'Content-Type': 'application/json',
      },
      timeout: TIMEOUT_DEFAULT,
    };

    this.axios = axios.create(config);

    this.axios.interceptors.request.use(
      async (requestConfig) => {
        await checkToken();
        setToken(requestConfig);
        showProgressBar();
        return requestConfig;
      },
      async (error) => {
        errorHideProgressBar();
        return Promise.reject(error);
      },
    );

    this.axios.interceptors.response.use(
      (response) => {
        hideProgressBar();
        resetSessionTimer();
        return response;
      },
      (error: AxiosError) => this.onResponseError(error, this.axios),
    );
  }

  protected onResponseError(error: AxiosError, axiosInstance: AxiosInstance): unknown {
    errorHideProgressBar();
    const originalRequest = error.config as ExtendedInternalAxiosRequestConfig;
    if (error?.response?.status === 401 && !originalRequest._retry) {
      // Wir gehen davon aus, dass der 401 von einem abgelaufenen Token herrührt, daher versuchen wir es zu refreshen
      return handleInvalidToken(originalRequest, axiosInstance);
    }
    return Promise.reject(error);
  }

  protected async doRequest<T>(
    httpMethod: Method,
    url: string,
    config?: IHttpRequestConfig,
  ): Promise<T> {
    const response = await this.doRequestWithAxiosRes<T>(httpMethod, url, config);
    return response.data;
  }

  protected async doRequestWithAxiosRes<T>(
    httpMethod: Method,
    url: string,
    config?: IHttpRequestConfig,
  ): Promise<AxiosResponse<T, T>> {
    const requestConfig: RawAxiosRequestConfig = config ? this.buildAxiosConfig(config) : {};
    const request = this.axios.request<T>({
      ...requestConfig,
      method: httpMethod,
      url,
    });
    return await request;
  }

  protected buildAxiosConfig(config: IHttpRequestConfig): RawAxiosRequestConfig {
    return {
      params: config.params,
      data: config.data,
      ...config.config,
    };
  }

  get<T>(url: string, config?: IHttpRequestConfig): Promise<T> {
    return this.doRequest('GET', url, config);
  }

  getWithResponse<T>(url: string, config?: IHttpRequestConfig): Promise<AxiosResponse<T>> {
    return this.doRequestWithAxiosRes('GET', url, config);
  }

  post<T>(url: string, config?: IHttpRequestConfig): Promise<T> {
    return this.doRequest('POST', url, config);
  }

  postWithResponse<T>(url: string, config?: IHttpRequestConfig): Promise<AxiosResponse<T>> {
    return this.doRequestWithAxiosRes('POST', url, config);
  }

  put<T>(url: string, config?: IHttpRequestConfig): Promise<T> {
    return this.doRequest('PUT', url, config);
  }

  patch<T>(url: string, config?: IHttpRequestConfig): Promise<T> {
    return this.doRequest('PATCH', url, config);
  }

  delete<T>(url: string, config?: IHttpRequestConfig): Promise<T> {
    return this.doRequest('DELETE', url, config);
  }
}
